/**
 * Image optimization utilities
 * Provides functions to optimize images with modern formats and CDN transforms
 */

/**
 * Optimizes image URL for performance
 * @param {string} url - Original image URL
 * @param {Object} options - Optimization options
 * @param {number} options.width - Target width in pixels
 * @param {number} options.height - Target height in pixels
 * @param {string} options.fit - Fit mode: 'cover', 'contain', 'crop', 'scale-down'
 * @param {number} options.quality - Image quality 1-100
 * @param {string} options.format - Force format: 'auto', 'webp', 'avif', 'jpeg', 'png'
 * @returns {string} - Optimized image URL
 */
export const optimizeImage = (url, options = {}) => {
  if (!url || url.includes('placehold.co')) {
    return url;
  }

  const {
    width,
    height,
    fit = 'cover',
    quality = 80,
    format = 'auto'
  } = options;

  // Check if URL is from Unsplash
  if (url.includes('unsplash.com')) {
    const params = new URLSearchParams();
    if (width) params.set('w', width);
    if (height) params.set('h', height);
    params.set('q', quality);
    params.set('auto', format);
    params.set('fit', fit);

    return `${url.split('?')[0]}?${params.toString()}`;
  }

  // Check if URL is from Swell CDN (uses Cloudflare)
  if (url.includes('cdn.swell.store') || url.includes('swell.store')) {
    try {
      const urlObj = new URL(url);
      // Swell/Cloudflare supports width, height, quality, format params
      if (width) urlObj.searchParams.set('width', width);
      if (height) urlObj.searchParams.set('height', height);
      if (quality !== 80) urlObj.searchParams.set('quality', quality);
      // Request WebP format for better compression
      urlObj.searchParams.set('format', 'webp');
      return urlObj.toString();
    } catch (e) {
      return url;
    }
  }

  // Check if URL is from Cloudflare Images
  if (url.includes('imagedelivery.net')) {
    // Format: https://imagedelivery.net/{account}/{id}/{variant}
    const parts = url.split('/');
    if (parts.length >= 5) {
      const accountHash = parts[3];
      const imageId = parts[4];
      const variant = width && height ? `w=${width},h=${height},fit=${fit},q=${quality}` : 'public';
      return `https://imagedelivery.net/${accountHash}/${imageId}/${variant}`;
    }
  }

  // For other CDN images, try to append query parameters
  try {
    const urlObj = new URL(url);
    if (width) urlObj.searchParams.set('w', width);
    if (height) urlObj.searchParams.set('h', height);
    if (quality !== 80) urlObj.searchParams.set('q', quality);
    if (format !== 'auto') urlObj.searchParams.set('format', format);
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails, return original
    return url;
  }
};

/**
 * Generate srcset for responsive images
 * @param {string} url - Original image URL
 * @param {Array<number>} widths - Array of widths for srcset
 * @returns {string} - srcset string
 */
export const generateSrcSet = (url, widths = [400, 600, 800, 1200]) => {
  if (!url || url.includes('placehold.co')) {
    return '';
  }

  return widths
    .map(width => `${optimizeImage(url, { width, quality: 80 })} ${width}w`)
    .join(', ');
};

/**
 * Get optimal image sizes for different breakpoints
 * @param {string} type - Component type: 'card', 'hero', 'thumbnail', 'detail'
 * @returns {string} - sizes attribute value
 */
export const getImageSizes = (type) => {
  switch (type) {
    case 'card':
      return '(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw';
    case 'hero':
      return '100vw';
    case 'thumbnail':
      return '(max-width: 640px) 80px, 100px';
    case 'detail':
      return '(max-width: 768px) 100vw, 50vw';
    default:
      return '100vw';
  }
};
